import {
  ApplicationConfig,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
  PLATFORM_ID,
  InjectionToken,
  REQUEST,
} from '@angular/core';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
  provideHttpClient,
  withInterceptors,
  withFetch,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { apiRequestInterceptor } from './interceptors/api-request.interceptor';
import { CmsService } from '@services/cms/cms.service';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { CmsPageEnum } from '@models/copy/cms-page-map.model';
import { AppSettingsService } from '@services/app-settings/app-settings.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([apiRequestInterceptor]), withFetch()),
    provideAnimations(),
    CmsService,
    // Ensure CMS copy is loaded before the app bootstraps.
    provideAppInitializer(() => {
      const cmsService = inject(CmsService);
      const appSettingsService = inject(AppSettingsService);
      const platformId = inject(PLATFORM_ID);
      if (isPlatformServer(platformId)) {
        const request = inject(REQUEST, {
          optional: true,
        });
        const originalUrl = inject(
          'ORIGINAL_URL' as unknown as InjectionToken<string>,
          { optional: true },
        );

        console.log('[APP_INITIALIZER] Request URL:', request?.url);
        console.log('[APP_INITIALIZER] Original URL:', originalUrl);
        // Prefer a real request object when available, otherwise fall back to
        // the ORIGINAL_URL provider supplied by the server renderer.
        const urlSource = request?.url ?? originalUrl ?? undefined;
        if (urlSource) {
          // Attempt to parse as an absolute URL first; if that fails, treat
          // the value as a path and parse with a safe base so we can read
          // query parameters (e.g. '/not-found?x=1').
          let parsedUrl: URL | undefined;
          try {
            parsedUrl = new URL(String(urlSource));
          } catch {
            try {
              parsedUrl = new URL(String(urlSource), 'http://localhost');
            } catch {
              parsedUrl = undefined;
            }
          }

          if (parsedUrl) {
            console.log('[APP_INITIALIZER] Parsed URL:', parsedUrl);
            const myParam = parsedUrl.searchParams.get('brand');
            if (myParam) {
              appSettingsService.brandSig.set(
                appSettingsService.ensureBrandEnumValue(myParam),
              );
            } else {
              console.log(
                '[APP_INITIALIZER] No brand query parameter found, setting brand by hostname:',
                parsedUrl.hostname,
              );
              appSettingsService.setBrandByHostname(parsedUrl.hostname);
            }
          }
        }
        // Preload common copy on the server when we have an incoming request
        // available. Also invoke preload on the browser so the client-side
        // `CmsService` can read any `TransferState` (ng-state) populated by
        // the server and populate its signals before hydration clears the
        // DOM. Guard server behavior to only run when a per-request context
        // exists to avoid issuing requests with default brand during global
        // provider initialization.
        if (request || originalUrl) {
          return cmsService
            .preloadPageCopy(CmsPageEnum.APP_COMMON)
            .catch(() => Promise.resolve());
        }
      }

      if (isPlatformBrowser(platformId)) {
        // On the browser, attempt to preload so the client-side service will
        // call `getCmsCopy` which leverages Angular's HTTP TransferState
        // cache (no extra TransferState keys are created). This allows the
        // client to populate signals from the server-provided response and
        // avoid clearing server-rendered content during hydration.
        return cmsService
          .preloadPageCopy(CmsPageEnum.APP_COMMON)
          .catch(() => Promise.resolve());
      }
      return Promise.resolve();
    }),
    provideRouter(routes()),
    provideClientHydration(withEventReplay()),
    // Uncomment below and import modules from root if needed for bootstrap items
    // importProvidersFrom(),
  ],
};
